import { useCallback, useEffect, useMemo, useState } from "react";
import { posMonitorApi, type PosMonitorDashboard } from "../../api/posMonitorApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessPosMonitor } from "../../auth/roles";

function toLocalDate(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function severityBadgeClass(severity: string) {
  const s = String(severity || "").toUpperCase();
  if (s === "HIGH" || s === "CRITICAL") return "text-bg-danger";
  if (s === "MEDIUM") return "text-bg-warning";
  return "text-bg-secondary";
}

export default function PosMonitorPage() {
  const { token, user } = useAuth();
  const canView = canAccessPosMonitor(user?.role);
  const [data, setData] = useState<PosMonitorDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [onlyFiscalErrors, setOnlyFiscalErrors] = useState(false);
  const [onlyOpenSessions, setOnlyOpenSessions] = useState(false);
  const [alertSeverityFilter, setAlertSeverityFilter] = useState<"ALL" | "HIGH" | "MEDIUM" | "LOW">("ALL");

  const load = useCallback(async () => {
    if (!token || !canView) return;
    setLoading(true);
    setError("");
    try {
      const res = await posMonitorApi.getDashboard(token);
      setData(res);
      setLastUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      const msg = e?.message || "Errore caricamento monitor POS";
      if (msg.includes("403")) {
        setError("Accesso riservato a Manager, Admin o Operatore.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [token, canView]);

  useEffect(() => {
    if (!canView) {
      setError("Accesso riservato a Manager, Admin o Operatore.");
      return;
    }
    void load();
  }, [load, canView]);

  useEffect(() => {
    if (!autoRefresh || !canView) return;
    const timer = window.setInterval(() => {
      void load();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, load, canView]);

  const devices = useMemo(() => {
    const rows = data?.devices ?? [];
    return rows.filter((d) => {
      if (onlyFiscalErrors && Number(d.fiscalErrors || 0) <= 0) return false;
      if (onlyOpenSessions && !d.openSessionId) return false;
      return true;
    });
  }, [data, onlyFiscalErrors, onlyOpenSessions]);
  const alerts = useMemo(() => {
    const rows = data?.alerts ?? [];
    if (alertSeverityFilter === "ALL") return rows;
    return rows.filter((a) => String(a.severity || "").toUpperCase() === alertSeverityFilter);
  }, [data, alertSeverityFilter]);

  if (!canView) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-warning mb-0">
          <strong>Monitor POS non disponibile</strong> per il tuo profilo ({user?.role || "OPERATOR"}).
          Serve un account <strong>Manager</strong>, <strong>Admin</strong> o <strong>Operatore</strong>.
        </div>
      </div>
    );
  }

  function exportDevicesCsv() {
    const header = [
      "posCode",
      "location",
      "sessionStatus",
      "lastSaleAt",
      "lastSeenAt",
      "fiscalPending",
      "fiscalErrors",
    ];
    const rows = devices.map((d) => [
      d.posCode,
      d.location || "",
      d.openSessionId ? "OPEN" : "CLOSED",
      d.lastSaleAt || "",
      d.lastSeenAt || "",
      String(d.fiscalPending ?? 0),
      String(d.fiscalErrors ?? 0),
    ]);
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((line) => line.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `pos-monitor-devices-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportAlertsCsv() {
    const header = ["severity", "title", "message", "status", "lastSeenAt", "posDeviceId"];
    const rows = alerts.map((a) => [
      String(a.severity || ""),
      String(a.title || ""),
      String(a.message || ""),
      String(a.status || ""),
      String(a.lastSeenAt || ""),
      String(a.posDeviceId || ""),
    ]);
    const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((line) => line.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `pos-monitor-alerts-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-0">Monitor POS Centrale</h2>
          <small className="text-muted">
            Stato casse, sync fiscale e alert operativi
            {lastUpdatedAt ? ` - Ultimo aggiornamento: ${toLocalDate(lastUpdatedAt)}` : ""}
          </small>
        </div>
        <button className="btn btn-outline-primary" onClick={() => void load()} disabled={loading}>
          {loading ? "Aggiornamento..." : "Aggiorna"}
        </button>
      </div>
      <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
        <div className="form-check">
          <input
            id="posMonitorAutoRefresh"
            className="form-check-input"
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="posMonitorAutoRefresh">
            Auto-refresh 30s
          </label>
        </div>
        <div className="form-check">
          <input
            id="posMonitorOnlyFiscalErrors"
            className="form-check-input"
            type="checkbox"
            checked={onlyFiscalErrors}
            onChange={(e) => setOnlyFiscalErrors(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="posMonitorOnlyFiscalErrors">
            Solo casse con errori fiscali
          </label>
        </div>
        <div className="form-check">
          <input
            id="posMonitorOnlyOpenSessions"
            className="form-check-input"
            type="checkbox"
            checked={onlyOpenSessions}
            onChange={(e) => setOnlyOpenSessions(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="posMonitorOnlyOpenSessions">
            Solo sessioni aperte
          </label>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="posMonitorSeverityFilter" className="small text-muted mb-0">
            Severity alert
          </label>
          <select
            id="posMonitorSeverityFilter"
            className="form-select form-select-sm"
            style={{ width: 140 }}
            value={alertSeverityFilter}
            onChange={(e) => setAlertSeverityFilter(e.target.value as "ALL" | "HIGH" | "MEDIUM" | "LOW")}
          >
            <option value="ALL">ALL</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </div>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <div className="row g-3 mb-3">
        <div className="col-12 col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">POS attivi</div>
              <div className="fs-4 fw-bold">{data?.totals?.devices ?? 0}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Sessioni aperte</div>
              <div className="fs-4 fw-bold">{data?.totals?.withOpenSession ?? 0}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Fiscale pending</div>
              <div className="fs-4 fw-bold">{data?.totals?.fiscalPending ?? 0}</div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="text-muted small">Fiscale error</div>
              <div className="fs-4 fw-bold text-danger">{data?.totals?.fiscalErrors ?? 0}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="card-title mb-0">Casse</h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={exportDevicesCsv}>
              Export CSV
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>POS</th>
                  <th>Sede</th>
                  <th>Sessione</th>
                  <th>Ultima vendita</th>
                  <th>Last seen</th>
                  <th>Fiscal pending</th>
                  <th>Fiscal error</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      Nessun dato disponibile
                    </td>
                  </tr>
                ) : (
                  devices.map((d) => (
                    <tr key={d.posDeviceId}>
                      <td className="fw-semibold">{d.posCode}</td>
                      <td>{d.location || "-"}</td>
                      <td>{d.openSessionId ? "APERTA" : "CHIUSA"}</td>
                      <td>{toLocalDate(d.lastSaleAt)}</td>
                      <td>{toLocalDate(d.lastSeenAt)}</td>
                      <td>{d.fiscalPending}</td>
                      <td className={d.fiscalErrors > 0 ? "text-danger fw-semibold" : ""}>{d.fiscalErrors}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="card-title mb-0">Alert operativi</h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={exportAlertsCsv}>
              Export CSV alert
            </button>
          </div>
          <div className="d-grid gap-2">
            {alerts.length === 0 ? <div className="text-muted">Nessun alert aperto</div> : null}
            {alerts.map((a) => (
              <div key={a.id} className="border rounded p-2">
                <div className="d-flex justify-content-between align-items-center">
                  <strong>{a.title}</strong>
                  <span className={`badge ${severityBadgeClass(a.severity)}`}>{a.severity}</span>
                </div>
                <div className="small">{a.message}</div>
                <div className="small text-muted">Ultimo evento: {toLocalDate(a.lastSeenAt)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
